/**
 * Utility functions for the Consults component
 */

/**
 * Calculate date range based on view mode
 * @param {string} viewMode - "weekly" or "daily" view mode
 * @returns {Object} Date range information
 */
const formatWeekRangeLabel = (startDate, endDate) => {
  const sameYear = startDate.getFullYear() === endDate.getFullYear();

  const baseFormat = { month: "short", day: "numeric" };
  const startLabel = startDate.toLocaleDateString("en-US", {
    ...baseFormat,
    ...(sameYear ? {} : { year: "numeric" }),
  });
  const endLabel = endDate.toLocaleDateString("en-US", {
    ...baseFormat,
    year: "numeric",
  });

  const suffix = sameYear ? `, ${startDate.getFullYear()}` : "";
  return `${startLabel} - ${endLabel}${suffix}`;
};

const formatDayLabel = (date) => {
  return date.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const formatLocalISODate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const getDateRange = (viewMode, offset = 0) => {
  const today = new Date();
  const currentDay = today.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday

  if (viewMode === "weekly") {
    const showNextWeek = currentDay === 6 || currentDay === 0;

    let daysToMonday;
    if (showNextWeek) {
      daysToMonday = currentDay === 6 ? 2 : 1;
    } else {
      daysToMonday = -(currentDay - 1);
    }

    const monday = new Date(today);
    monday.setDate(today.getDate() + daysToMonday);
    monday.setHours(0, 0, 0, 0);

    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    sunday.setHours(23, 59, 59, 999);

    if (offset !== 0) {
      monday.setDate(monday.getDate() + offset * 7);
      sunday.setDate(sunday.getDate() + offset * 7);
    }

    return {
      startDate: new Date(monday),
      endDate: new Date(sunday),
      viewLabel: formatWeekRangeLabel(monday, sunday),
      isNextWeek: showNextWeek && offset === 0,
    };
  }

  const startDate = new Date(today);
  const isWeekend = currentDay === 6 || currentDay === 0;
  const weekendFallbackOffset = currentDay === 6 ? 2 : currentDay === 0 ? 1 : 0;
  const dailyOffset =
    offset === 0 && isWeekend ? weekendFallbackOffset : offset;
  startDate.setDate(today.getDate() + dailyOffset);
  startDate.setHours(0, 0, 0, 0);

  const endDate = new Date(startDate);
  endDate.setHours(23, 59, 59, 999);

  return {
    startDate,
    endDate,
    viewLabel: formatDayLabel(startDate),
    isNextWeek: false,
  };
};

export const getDateRangeOptions = (
  viewMode,
  { pastWeeks = 12, pastDays = 30 } = {},
) => {
  const totalUnits = viewMode === "weekly" ? pastWeeks : pastDays;
  const options = [];

  for (let step = 0; step <= totalUnits; step++) {
    const offset = -step;
    const range = getDateRange(viewMode, offset);
    options.push({ ...range, offset });
  }

  return options;
};

/**
 * Format date as a string in YYYY-MM-DD format
 * @param {Date} date - Date object to format
 * @returns {string} Formatted date string
 */
export const formatDateString = (date) => {
  // Add a safety check
  if (!date) {
    console.warn("Attempted to format undefined or null date");
    return "";
  }
  return formatLocalISODate(date);
};

/**
 * Filter schedule data based on search term and view mode
 * @param {Array} scheduleData - Array of schedule sections
 * @param {string} searchTerm - Search term
 * @param {boolean} searchMode - Whether search mode is active
 * @param {Array} searchResults - Search results
 * @param {string} viewMode - "daily" or "weekly"
 * @returns {Array} Filtered schedule data
 */
export const getFilteredScheduleData = (
  scheduleData,
  searchTerm,
  searchMode,
  searchResults,
  viewMode,
  selectedDate,
) => {
  console.log("getFilteredScheduleData called", {
    scheduleDataLength: scheduleData?.length,
    searchMode,
    viewMode,
  });

  // If we're in search mode, return search results directly without further filtering
  if (searchMode) {
    return searchResults || [];
  }

  // Early return for empty data
  if (
    !scheduleData ||
    !Array.isArray(scheduleData) ||
    scheduleData.length === 0
  ) {
    console.log("getFilteredScheduleData: returning empty array");
    return [];
  }

  // Get data potentially filtered by search term first
  let filteredData;

  if (!searchTerm || !searchTerm.trim()) {
    // No search term, use full schedule data
    filteredData = scheduleData;
  } else {
    // Filter by search term
    filteredData = scheduleData
      .map((section) => {
        if (!section || !section.data || !Array.isArray(section.data)) {
          return null;
        }

        const filteredPatients = section.data.filter(
          (patient) =>
            patient &&
            patient.name &&
            (patient.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
              (patient.procedureType &&
                patient.procedureType
                  .toLowerCase()
                  .includes(searchTerm.toLowerCase()))),
        );

        if (filteredPatients.length === 0) {
          return null;
        }

        return {
          ...section,
          data: filteredPatients,
        };
      })
      .filter(Boolean);
  }

  // Ensure filteredData is an array
  if (!Array.isArray(filteredData)) {
    return [];
  }

  // Apply view mode filtering (only for non-search data)
  if (viewMode === "daily") {
    // Use provided date or default to today in YYYY-MM-DD format
    const targetDate = selectedDate || formatLocalISODate(new Date());
    // Filter to only show the selected day's section
    const dailyFiltered = filteredData.filter(
      (section) => section && section.date === targetDate,
    );
    console.log("Daily filter result:", dailyFiltered.length);
    return dailyFiltered;
  }

  // For weekly view, return all filtered data
  console.log("Weekly filter result:", filteredData.length);
  return filteredData;
};

/**
 * Create a before/after comparison image
 * @param {string} beforeImageUrl - URL of the before image
 * @param {string} afterImageUrl - URL of the after image
 * @returns {Promise<string>} Promise resolving to data URL of the comparison image
 */
export const createBeforeAfterComparison = (beforeImageUrl, afterImageUrl) => {
  return new Promise((resolve, reject) => {
    try {
      // Make sure we're in a browser environment
      if (typeof window === "undefined" || !window.document) {
        throw new Error("Canvas is only available in browser environments");
      }

      // Create canvas element
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      // Create image elements using the browser's Image constructor
      const beforeImg = new window.Image();
      const afterImg = new window.Image();

      // Counter to track loaded images
      let loadedImages = 0;

      // Function to draw once both images are loaded
      const drawImagesWhenReady = () => {
        loadedImages++;

        if (loadedImages === 2) {
          // Set canvas dimensions
          const imgHeight = Math.max(beforeImg.height, afterImg.height);
          const imgWidth = Math.max(beforeImg.width, afterImg.width);

          // Make canvas wide enough for both images side by side
          canvas.width = imgWidth * 2;
          canvas.height = imgHeight;

          // Draw before image on left side
          ctx.drawImage(beforeImg, 0, 0, imgWidth, imgHeight);

          // Draw after image on right side
          ctx.drawImage(afterImg, imgWidth, 0, imgWidth, imgHeight);

          // Add semi-transparent overlay for text
          ctx.fillStyle = "rgba(0, 0, 0, 0.6)";
          ctx.fillRect(10, imgHeight - 50, 120, 40);
          ctx.fillRect(imgWidth + 10, imgHeight - 50, 120, 40);

          // Add text
          ctx.fillStyle = "white";
          ctx.font = "bold 24px Arial";
          ctx.fillText("Before", 30, imgHeight - 20);
          ctx.fillText("After", imgWidth + 30, imgHeight - 20);

          // Get the final image as data URL
          const resultImageUrl = canvas.toDataURL("image/jpeg", 0.9);
          resolve(resultImageUrl);
        }
      };

      // Setup image load events
      beforeImg.onload = drawImagesWhenReady;
      afterImg.onload = drawImagesWhenReady;
      beforeImg.onerror = (e) =>
        reject(`Error loading before image: ${e.message}`);
      afterImg.onerror = (e) =>
        reject(`Error loading after image: ${e.message}`);

      // Set cross-origin to anonymous to avoid CORS issues
      beforeImg.crossOrigin = "anonymous";
      afterImg.crossOrigin = "anonymous";

      // Set image sources to start loading
      beforeImg.src = beforeImageUrl;
      afterImg.src = afterImageUrl;
    } catch (error) {
      reject(error);
    }
  });
};

/**
 * Find the section index for today's date in schedule data
 * @param {Array} displayData - Schedule data to search
 * @returns {number} Index of today's section or -1 if not found
 */
export const findTodaySectionIndex = (displayData) => {
  if (!displayData || displayData.length === 0) {
    return -1;
  }

  const todayStr = formatLocalISODate(new Date());
  return displayData.findIndex((section) => section.date === todayStr);
};

export default {
  getDateRange,
  getDateRangeOptions,
  formatDateString,
  getFilteredScheduleData,
  createBeforeAfterComparison,
  findTodaySectionIndex,
};
